import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Factory, Globe2 } from "lucide-react";

type Props = {
  imageSrc?: string; // optional if you want to pass an image
};

export default function GlobalLeaderSection({ imageSrc = "/about.jpg" }: Props) {
  return (
    <section className="relative bg-white py-16 md:py-24 overflow-hidden">
      {/* Soft background accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-10 bg-[#F47C20]" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-10 bg-[#E83E6F]" />

      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT: Image / Visual */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-black/5">
              {/* Image */}
              <img
                // src={imageSrc}
                src="https://tse4.mm.bing.net/th/id/OIP.dDl_lThR6tTK6JIOrvYilgHaFA?rs=1&pid=ImgDetMain&o=7&rm=3"
                alt="Manufacturing Facility"
                className="h-[420px] w-full object-cover"
                loading="lazy"
              />

              {/* Dark gradient overlay for premium feel */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

              {/* Corner badge */}
              <div className="absolute top-5 left-5 rounded-full bg-white/85 backdrop-blur px-4 py-2 border border-black/10">
                <p className="text-xs font-semibold text-[#2B2B2B] tracking-wide">
                  STATE-OF-THE-ART FACILITY
                </p>
              </div>

              {/* Bottom stat cards */}
              <div className="absolute bottom-5 left-5 right-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Factory size={18} className="text-white" />
                    <p className="text-xs text-white/80">Manufacturing</p>
                  </div>
                  <p className="mt-2 text-sm font-semibold">Modern Production</p>
                </div>

                <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-white" />
                    <p className="text-xs text-white/80">Quality</p>
                  </div>
                  <p className="mt-2 text-sm font-semibold">Rigorous Controls</p>
                </div>

                <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Globe2 size={18} className="text-white" />
                    <p className="text-xs text-white/80">Global</p>
                  </div>
                  <p className="mt-2 text-sm font-semibold">Export Ready</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Content */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.05 }}
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            {/* Section label */}
            <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.02] px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-gradient-to-r from-[#F47C20] to-[#E83E6F]" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2B2B2B]/80">
                Global Leadership
              </span>
            </div>

            {/* Heading */}
            <h2 className="mt-6 text-3xl md:text-4xl font-extrabold leading-tight text-[#1B1B1B]">
              Global Leader{" "}
              <span className="text-[#C62828]">in Supplement Manufacturing</span>
            </h2>

            {/* Body copy */}
            <p className="mt-5 text-base leading-relaxed text-[#2B2B2B]/80">
              Amera Export leads the dietary supplement industry by integrating
              advanced technology and expert craftsmanship to deliver
              unparalleled quality. Our state-of-the-art facilities and rigorous
              quality controls enable innovative manufacturing for renowned
              global brands. We consistently exceed expectations through
              continuous improvement and steadfast commitment to excellence,
              delivering excellence every step worldwide, always.
            </p>

            {/* Small highlights */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-[#1B1B1B]">
                  Innovation-driven production
                </p>
                <p className="mt-1 text-sm text-[#2B2B2B]/75">
                  Modern processes built for scalable global supply.
                </p>
              </div>
              <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-[#1B1B1B]">
                  Strict quality assurance
                </p>
                <p className="mt-1 text-sm text-[#2B2B2B]/75">
                  Controls and checks at every manufacturing stage.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-9">
              <motion.a
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                href="/about"
                className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white
                           bg-gradient-to-r from-[#F47C20] to-[#E83E6F]
                           shadow-md shadow-black/15 hover:opacity-95 transition"
              >
                Read More
                <ArrowUpRight size={18} />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}